import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Trash2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Botão de exclusão (soft delete) visível APENAS para diretor/gerente.
 * Suporta confirmação obrigatória + aviso adicional opcional.
 *
 * Props:
 *   url          endpoint DELETE (ex: "/leads/abc")
 *   label        identificação para a confirmação (ex: "Lead João Silva")
 *   onDeleted    callback após sucesso (geralmente recarrega lista)
 *   warningText  texto extra para casos sensíveis ("contrato com equipamento ativo")
 *   size         passthrough para Button
 *   variant      passthrough para Button
 *   testId       passthrough para data-testid
 */
export function DeleteButton({
  url, label, onDeleted, warningText,
  size = "sm", variant = "outline", testId,
  children, iconOnly = false,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // RBAC FRONTEND — não renderiza para outros perfis
  if (!user || !["diretor", "gerente"].includes(user.role)) return null;

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete(url);
      toast.success("Excluído com sucesso");
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha ao excluir";
      toast.error(typeof detail === "string" ? detail : "Falha ao excluir");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size={size} variant={variant}
          data-testid={testId || "delete-btn"}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
          title="Excluir"
        >
          <Trash2 size={iconOnly ? 14 : 12} className={iconOnly ? "" : "mr-1.5"} />
          {!iconOnly && (children || "Excluir")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              Excluir <strong className="text-slate-900">{label}</strong>?
            </span>
            <span className="block text-sm text-red-700">Esta ação não pode ser desfeita.</span>
            {warningText && (
              <span className="block text-sm bg-amber-50 border border-amber-200 rounded p-2 text-amber-900 mt-2">
                ⚠️ {warningText}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="delete-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleDelete(); }}
            disabled={busy}
            data-testid="delete-confirm"
            className="bg-red-600 hover:bg-red-700"
          >
            {busy && <Loader2 className="animate-spin mr-2" size={14} />}
            Confirmar exclusão
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
